import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetHolidaysQuery, useGetHolidayStatsQuery } from 'redux/features/holidayApi';
import { useGetSettingsQuery } from 'redux/features/settings/settingsApi';
import { useGetTaxesQuery } from 'redux/features/taxApi';
import { useGetMonthlyTimeLogsByUserIdQuery } from 'redux/features/timeLogApi';
import { useGetUserByIdQuery } from 'redux/features/users/userApi';
import useUser from 'hooks/useUser';

import Breadcrumb from 'components/ui/Breadcrumb';
import Loader from 'components/ui/Loader';
import { useGetPaySlipQuery, useLazyCalculateSalaryQuery } from 'redux/features/salaryApi';
import Icon from '../../components/AppIcon';
import CommandPalette from '../../components/ui/CommandPalette';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import MonthlyLogsTable from './components/MonthlyLogsTable';
import PendingSalaryItems, { usePendingSalaryItems } from './components/PendingSalaryItems';
import SalaryBreakdownModal from './components/SalaryBreakdownModal';

import ConfirmationModal from '../../components/ui/ConfirmationModal';
const EmployeeTimeLogsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location?.search);
  const id = urlParams?.get('id');
  const currentUser = useUser();
  const fromAttendance = location?.state?.navFrom === '/attendance-salary';

  // month / year / calculate can come from the URL (e.g. coming back from the log update request page)
  const [selectedMonth, setSelectedMonth] = useState(
    Number(urlParams.get('month')) || new Date().getMonth() + 1,
  );
  const [selectedYear, setSelectedYear] = useState(
    Number(urlParams.get('year')) || new Date().getFullYear(),
  );
  const autoCalculate = urlParams.get('calculate') === '1';

  const [employeeData, setEmployeeData] = useState(null);
  const [monthlyLogs, setMonthlyLogs] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [monthlyEvents, setMonthlyEvents] = useState([]);

  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [modalViewMode, setModalViewMode] = useState('BREAKDOWN');

  const [salaryData, setSalaryData] = useState(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);

  /* ===================== RTK QUERIES ===================== */

  const { data: monthlyTimeData, isLoading: monthlyRecordIsLoading } =
    useGetMonthlyTimeLogsByUserIdQuery(
      { id, month: selectedMonth, year: selectedYear },
      { skip: !id, refetchOnMountOrArgChange: true },
    );

  const { data: salarySlip, refetch: refetchSalarySlip } = useGetPaySlipQuery(
    {
      month: selectedMonth,
      year: selectedYear,
      userId: employeeData?.id,
    },
    { skip: !employeeData?.id },
  );

  const [calculateSalary, { data: calculatedSalaryData, isFetching: calculateSalaryIsLoading }] =
    useLazyCalculateSalaryQuery({ refetchOnMountOrArgChange: true });

  const { data: userData } = useGetUserByIdQuery(parseInt(id), { refetchOnMountOrArgChange: true });
  const userSalaryIsConfigured = userData?.data?.salary?.basic_salary > 0;
  const { data: monthlyEventsData } = useGetHolidaysQuery({
    month: selectedMonth,
    year: selectedYear,
  });

  const { data: holidayStats } = useGetHolidayStatsQuery(
    { month: selectedMonth, year: selectedYear },
    { refetchOnMountOrArgChange: true },
  );

  const { data: Taxes } = useGetTaxesQuery();
  const { data: settings } = useGetSettingsQuery();

  /* ===================== EFFECTS ===================== */

  useEffect(() => {
    if (userData?.data) setEmployeeData(userData.data);
    if (monthlyTimeData?.data) {
      setMonthlyLogs(monthlyTimeData.data.logs || []);
      setMonthlySummary(monthlyTimeData.data.summary || null);
    }
    if (monthlyEventsData) setMonthlyEvents(monthlyEventsData);

    // Only set salaryData if slip exists
    if (salarySlip?.status == 'success') {
      setSalaryData(salarySlip.data);
    }
  }, [userData, monthlyTimeData, monthlyEventsData, salarySlip]);

  const isSalaryApproved = !!salarySlip?.data?.is_approved;

  /* ===================== HANDLERS ===================== */

  const pending = usePendingSalaryItems({
    userId: employeeData?.id,
    month: selectedMonth,
    year: selectedYear,
  });

  // Fresh calculation + open the salary modal
  const runSalaryCalculation = async () => {
    // Always trigger real-time calculation when slip is not approved (draft or new).
    // Wait for it (never use the cached result) so the modal opens with up-to-date figures.
    if (!salarySlip?.data || !salarySlip?.data?.is_approved) {
      await calculateSalary(
        {
          month: selectedMonth,
          year: selectedYear,
          id: employeeData?.id,
        },
        false,
      );
    }

    setModalViewMode('BREAKDOWN');
    setShowSalaryModal(true);
  };

  const handleCalculateSalary = async () => {
    if (!userSalaryIsConfigured) {
      setShowConfigModal(true);
      return;
    }
    // Approved salary is only viewed, nothing to clear
    if (!isSalaryApproved) {
      // Re-read from the server so the check is never based on stale data
      const openItems = await pending.refresh();
      if (openItems > 0) {
        setShowPendingModal(true);
        return;
      }
    }
    runSalaryCalculation();
  };

  // Once HR has cleared everything in the pending modal, open the (freshly calculated) salary
  useEffect(() => {
    if (showPendingModal && pending.total === 0) {
      setShowPendingModal(false);
      runSalaryCalculation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPendingModal, pending.total]);

  // Back from the log update request page: re-run the pending check / calculation with fresh data
  const autoCalculated = useRef(false);
  useEffect(() => {
    if (!autoCalculate || autoCalculated.current || !employeeData?.id || !userData) return;
    autoCalculated.current = true;
    navigate(
      `/employee-management/employee-time-logs?id=${id}&month=${selectedMonth}&year=${selectedYear}`,
      {
        replace: true,
      },
    );
    handleCalculateSalary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCalculate, employeeData?.id, userData]);

  const handleMonthlyTableChange = (year, month) => {
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  const handleBack = () => {
    navigate(-1);
  };

  /* ===================== UI ===================== */

  if (!employeeData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background ">
      <Header />
      <Sidebar />
      <CommandPalette />

      <main className="lg:ml-60 pt-16">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Breadcrumb
            items={[
              fromAttendance
                ? { label: 'Attendance', path: '/attendance-salary' }
                : { label: 'Employees', path: '/employee-management' },
              { label: 'Time Logs & Salary' },
            ]}
          />

          <motion.button
            onClick={handleBack}
            className="flex items-center space-x-2 my-5 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Back</span>
          </motion.button>

          {/* Employee Card */}
          <motion.div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                <Icon name="User" size={24} />
                <span>Employee Details</span>
              </h2>
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-medium text-lg">
                      {employeeData?.first_name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {employeeData?.first_name + ' ' + employeeData?.last_name}
                    </h3>
                    <p className="text-sm text-gray-500">ID: {employeeData?.employee_id}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {isSalaryApproved && (
                    <button
                      onClick={() => {
                        setModalViewMode('SLIP');
                        setShowSalaryModal(true);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <Icon name="Notebook" size={16} />
                      Slip
                    </button>
                  )}

                  <button
                    onClick={handleCalculateSalary}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {calculateSalaryIsLoading ? (
                      <Icon className="animate-spin" name="Loader" size={16} />
                    ) : (
                      <Icon name="Banknote" size={16} />
                    )}
                    <span className="hidden sm:inline">
                      {isSalaryApproved ? 'View Salary' : 'Calculate Salary'}
                    </span>
                  </button>

                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        employeeData?.session_status === 'ACTIVE'
                          ? 'bg-green-500 animate-pulse'
                          : 'bg-gray-400'
                      }`}
                    ></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Session Status</p>
                      <p
                        className={`text-sm ${
                          employeeData?.session_status === 'ACTIVE'
                            ? 'text-green-600'
                            : 'text-gray-600'
                        }`}
                      >
                        {employeeData?.session_status?.toLowerCase() || 'Inactive'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {monthlyRecordIsLoading ? (
            <Loader />
          ) : (
            <MonthlyLogsTable
              monthlyLogs={monthlyLogs}
              summary={monthlySummary}
              onMonthChange={handleMonthlyTableChange}
              userId={employeeData?.id}
              overtimeEnabled={employeeData?.overtime_enabled}
            />
          )}
        </motion.div>
      </main>

      {!calculateSalaryIsLoading && (
        <SalaryBreakdownModal
          calculatedSalaryData={calculatedSalaryData?.data}
          calculateSalaryIsLoading={calculateSalaryIsLoading}
          salaryData={salaryData}
          settings={settings?.data}
          monthlyLogs={monthlyLogs}
          holidayStats={holidayStats}
          monthlyEvents={monthlyEvents}
          isOpen={showSalaryModal}
          onClose={() => setShowSalaryModal(false)}
          employeeData={employeeData}
          taxes={Taxes}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          refetchSalarySlip={refetchSalarySlip}
          initialViewMode={modalViewMode}
          userRole={currentUser?.role}
          isSalaryApproved={isSalaryApproved}
        />
      )}

      <PendingSalaryItems
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
        employee={employeeData}
        monthlyLogs={monthlyLogs}
        pendingUnplanned={pending.pendingUnplanned}
        pendingRequests={pending.pendingRequests}
        month={selectedMonth}
        year={selectedYear}
      />

      <ConfirmationModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Salary Not Configured"
        message="Salary is not configured for this user. Would you like to configure it now?"
        onConfirm={() => {
          setShowConfigModal(false);
          navigate(
            `/employee-management/employee-edit-full-page?id=${employeeData?.id}#salary-info`,
          );
        }}
      />
    </div>
  );
};

export default EmployeeTimeLogsPage;
